import type { User as PermissionsUser } from '@boqnet/permissions';

/**
 * Project role codes whose grants only exist while their LaunchDarkly flag is on,
 * mapped to the flag that opens each one. Currently empty — no project role is gated.
 *
 * Context: the seam is kept against an empty map rather than deleted, mirroring the Core
 * API's `IRoleFlagGate` (PE-637), so the next role to ship inert has somewhere to go. The
 * keys are the backend's wire codes, not `ProjRoles` members, because this map has to match
 * what the API actually sends.
 */
export const FLAG_GATED_PROJECT_ROLES = {} as const;

export type FlagGatedProjectRole = keyof typeof FLAG_GATED_PROJECT_ROLES;

export type RoleFlagState = Record<FlagGatedProjectRole, boolean>;

/**
 * The project roles whose flag is currently off, and which must therefore satisfy no
 * permission check at all.
 *
 * Context: exported because CASL is not the only consumer. The direct `rolesStore` role
 * checks read the auth store straight, so they have to consult the same withheld set or a
 * flag-gated role would stay live for them.
 */
export const withheldProjectRoles = (flags: RoleFlagState): ReadonlySet<string> => {
  const codes = Object.keys(FLAG_GATED_PROJECT_ROLES) as FlagGatedProjectRole[];

  return new Set(codes.filter((code) => !flags[code]));
};

/**
 * Removes flag-gated project roles from a user so the CASL ability is built as if the
 * user never held them. Returns a copy; the cached user keeps every role, so flipping a
 * flag on restores the grants without re-fetching.
 *
 * Context: the Core API gates the same roles at `FlagGatedProjectRoleReader` — one seam
 * where roles become permissions, with no flag awareness in any grant list — and the
 * frontend mirrors that so a role's abilities can be added without touching the flag
 * (boq-api-core PR #1323).
 *
 * With the map empty this returns at the `withheld.size === 0` guard every time, so the
 * filtering below is currently unexercised — the tests that covered it went with the role
 * they gated (PE-638), and the map is module-level so none can inject a synthetic one.
 * Whoever registers the next gated role brings its coverage back with it.
 */
export const withFlagGatedRolesRemoved = (
  user: PermissionsUser | null,
  flags: RoleFlagState,
): PermissionsUser | null => {
  if (!user) return null;

  const withheld = withheldProjectRoles(flags);
  if (withheld.size === 0 || !user.roles?.organisations) return user;

  return {
    ...user,
    roles: {
      ...user.roles,
      organisations: user.roles.organisations.map((org) => ({
        ...org,
        projects: (org.projects ?? []).map((project) => ({
          ...project,
          roles: project.roles.filter((role) => !withheld.has(role)),
        })),
      })),
    },
  };
};
